import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, Receipt, X } from 'lucide-react';

const InvoiceReceipt = ({ paymentData, onClose, onDownload }) => {
  const currentDate = new Date();
  const invoiceNumber = `INV-${currentDate.getFullYear()}${String(currentDate.getMonth() + 1).padStart(2, '0')}${String(currentDate.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
  
  // GST Calculation (18% total = 9% CGST + 9% SGST)
  const totalAmount = paymentData?.amount || 10;
  const taxableValue = 8.47;
  const cgst = 0.76; // 9%
  const sgst = 0.76; // 9%
  
  const handleDownload = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatePrintableInvoice());
    printWindow.document.close();
    printWindow.print();
    
    if (onDownload) onDownload(invoiceNumber);
  };

  const generatePrintableInvoice = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>GST Tax Invoice - ${invoiceNumber}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 40px; 
            color: #000;
          }
          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            border: 2px solid #000;
            padding: 20px;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .details-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            padding: 5px 0;
          }
          .details-label {
            font-weight: bold;
          }
          .section {
            margin: 20px 0;
            padding: 15px 0;
            border-bottom: 1px solid #ddd;
          }
          .table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          .table td {
            padding: 10px;
            border: 1px solid #ddd;
          }
          .table-header {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .total-row {
            background-color: #f0f0f0;
            font-weight: bold;
            font-size: 16px;
          }
          .checkmark {
            color: green;
            font-size: 18px;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div style="font-size: 20px; font-weight: bold; color: #1a1a1a; margin-bottom: 5px;">
              PLAYEDGE INNOVATIONS
            </div>
            <div class="title">GST TAX INVOICE</div>
          </div>
          
          <div class="section">
            <div class="details-row">
              <span class="details-label">Invoice No.:</span>
              <span>${invoiceNumber}</span>
            </div>
            <div class="details-row">
              <span class="details-label">Date:</span>
              <span>${currentDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
          
          <div class="section">
            <div class="details-row">
              <span class="details-label">GSTIN:</span>
              <span>06BWPPK6738M2ZP</span>
            </div>
            <div class="details-row">
              <span class="details-label">Place of Supply:</span>
              <span>Haryana</span>
            </div>
          </div>
          
          <div class="section">
            <div class="details-row">
              <span class="details-label">Customer:</span>
              <span>B2C User</span>
            </div>
          </div>
          
          <table class="table">
            <tr class="table-header">
              <td>Service</td>
              <td style="text-align: right;">Amount</td>
            </tr>
            <tr>
              <td>
                <strong>Registration Fee (GST Included)</strong><br>
                <small>HSN: 998365</small>
              </td>
              <td style="text-align: right;"></td>
            </tr>
            <tr>
              <td>Taxable Value</td>
              <td style="text-align: right;">₹${taxableValue.toFixed(2)}</td>
            </tr>
            <tr>
              <td>CGST 9%</td>
              <td style="text-align: right;">₹${cgst.toFixed(2)}</td>
            </tr>
            <tr>
              <td>SGST 9%</td>
              <td style="text-align: right;">₹${sgst.toFixed(2)}</td>
            </tr>
            <tr class="total-row">
              <td><span class="checkmark">✅</span> Total Amount</td>
              <td style="text-align: right;">₹${totalAmount.toFixed(2)}</td>
            </tr>
          </table>
          
          <div class="section">
            <div class="details-row">
              <span class="details-label">Payment Mode:</span>
              <span>UPI</span>
            </div>
            <div class="details-row">
              <span class="details-label">Txn ID:</span>
              <span>${paymentData?.transactionId || 'N/A'}</span>
            </div>
          </div>
          
          <div class="footer">
            <p><strong>Note:</strong> This is a system-generated GST invoice.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <Card className="gaming-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl text-white flex items-center space-x-2">
              <Receipt className="w-6 h-6 text-yellow-400" />
              <span>GST Tax Invoice</span>
            </CardTitle>
            <Button onClick={onClose} className="btn-secondary px-3 py-1">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Invoice Header */}
          <div className="text-center mb-6 pb-4 border-b border-gray-700">
            <h3 className="text-xl font-bold text-white mb-2">PLAYEDGE INNOVATIONS</h3>
            <h2 className="text-2xl font-bold text-yellow-400 mb-2">GST TAX INVOICE</h2>
          </div>

          {/* Invoice Details */}
          <div className="space-y-4 mb-6">
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">Invoice No.:</span>
              <span className="text-yellow-400">{invoiceNumber}</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">Date:</span>
              <span>{currentDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* GST Details */}
          <div className="bg-gray-800/50 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">GSTIN:</span>
              <span>06BWPPK6738M2ZP</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">Place of Supply:</span>
              <span>Haryana</span>
            </div>
          </div>

          {/* Customer */}
          <div className="mb-6">
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">Customer:</span>
              <span>B2C User</span>
            </div>
          </div>

          {/* Service Details Table */}
          <div className="bg-gray-800/30 rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-700/50 p-3 border-b border-gray-600">
              <div className="flex justify-between text-white font-semibold">
                <span>Service</span>
                <span>Amount</span>
              </div>
            </div>
            
            <div className="p-4 space-y-3">
              <div className="text-white">
                <div className="font-semibold">Registration Fee (GST Included)</div>
                <div className="text-sm text-gray-400">HSN: 998365</div>
              </div>
              
              <div className="flex justify-between text-gray-300 border-t border-gray-700 pt-3">
                <span>Taxable Value</span>
                <span>₹{taxableValue.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-gray-300">
                <span>CGST 9%</span>
                <span>₹{cgst.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-gray-300">
                <span>SGST 9%</span>
                <span>₹{sgst.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-white font-bold text-lg border-t-2 border-yellow-400 pt-3">
                <span className="flex items-center gap-2">
                  <span className="text-green-400">✅</span> Total Amount
                </span>
                <span className="text-yellow-400">₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-gray-800/50 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">Payment Mode:</span>
              <span>UPI</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span className="font-semibold">Txn ID:</span>
              <span className="text-yellow-400">{paymentData?.transactionId || 'N/A'}</span>
            </div>
          </div>

          {/* Note */}
          <div className="text-center text-sm text-gray-400 mb-6">
            <p><strong>Note:</strong> This is a system-generated GST invoice.</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button onClick={handleDownload} className="gaming-button flex-1">
              <Download className="w-4 h-4 mr-2" />
              Download Invoice
            </Button>
            <Button onClick={onClose} className="gaming-button-secondary flex-1">
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceReceipt;
